import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPStack, IPText } from 'baseComponents';
import { supportedUILanguages } from 'globalConstants';
import { globalTextStylesError } from 'globalStyles';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { useContext, useEffect, useRef } from 'react';
import { CountryIso2, ParsedCountry, PhoneInput, PhoneInputRefType } from 'react-international-phone';
import 'react-international-phone/style.css';
import './PhoneNumber.css';

export const DefaultCountryCode: string = 'gb';

const phoneUtil = PhoneNumberUtil.getInstance();

export const isValidNumber = (phone: string | undefined) => {
  try {
    if (!phone) return false;

    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const translateLanguageCodeToCountryCode = (langCode: string | undefined): CountryIso2 => {
  if (!langCode) return DefaultCountryCode;

  // This function is needed because the language code for Chinese is "zh" but the country code for China is "cn"
  if (langCode === 'zh') {
    return 'cn';
  }

  // This function is needed because the language code for English is "en" but the country code for United Kingdom is "gb"
  if (langCode === 'en') {
    return 'gb';
  }

  return langCode as CountryIso2;
};

interface IPhoneNumberProps {
  autoFocus?: boolean;
  label?: string;
  value?: string;
  placeholder?: string;
  errorMessage?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  defaultCountry?: CountryIso2;
  country?: CountryIso2;
  onChange: (value: string, country: ParsedCountry, isValid: boolean) => void;
}

const InternationalPhoneField = (props: IPhoneNumberProps) => {
  const appContext = useContext(AppContext);
  const phoneInputRef = useRef<PhoneInputRefType>(null);
  const theme = getTheme();

  useEffect(() => {
    if (props.autoFocus) {
      phoneInputRef.current?.focus();
    }
  }, [props.autoFocus]);

  useEffect(() => {
    if (!props.country || !phoneInputRef.current) return;

    const currentCountry = phoneInputRef.current.state.country.iso2;

    if (currentCountry !== props.country) {
      phoneInputRef.current.setCountry(props.country);
    }
  }, [props.country]);

  const checkPhoneValidity = (phone: string, inputValue: string): boolean => {
    if (props.required && !inputValue) {
      return false;
    }

    if (!props.required && !inputValue) {
      return true;
    }

    return isValidNumber(phone);
  };

  //
  // Main render
  //
  const foreColor = appContext.useDarkMode ? theme.palette.neutralLight : theme.palette.neutralDark;
  const backColor = appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.white;

  return (
    <IPStack>
      <IPStack.Item>
        <PhoneInput
          ref={phoneInputRef}
          preferredCountries={Array.from(supportedUILanguages).map(([code]) =>
            translateLanguageCodeToCountryCode(code),
          )}
          defaultCountry={props.defaultCountry || DefaultCountryCode}
          required={props.required}
          disabled={props.disabled}
          disableDialCodeAndPrefix={true}
          showDisabledDialCodeAndPrefix={true}
          placeholder={props.placeholder}
          value={props.value}
          onChange={(phone, meta) => {
            props.onChange(phone, meta.country, checkPhoneValidity(phone, meta.inputValue));
          }}
          inputStyle={{
            backgroundColor: backColor,
            color: foreColor,
          }}
          countrySelectorStyleProps={{
            buttonStyle: {
              backgroundColor: backColor,
              color: foreColor,
            },
            dropdownStyleProps: {
              style: {
                backgroundColor: backColor,
                color: foreColor,
              },
              listItemClassName: 'phone-dropdown-item',
            },
          }}
          dialCodePreviewStyleProps={{
            style: {
              backgroundColor: backColor,
              color: foreColor,
            },
          }}
        />
      </IPStack.Item>
      {props.label && (
        <IPStack.Item>
          <IPText block>{props.label}</IPText>
        </IPStack.Item>
      )}
      {props.errorMessage && (
        <IPStack.Item>
          <IPText variant="small" styles={globalTextStylesError}>
            {props.errorMessage}
          </IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default InternationalPhoneField;
